// src/utils/toast.js
import { toast } from "react-toastify";

/* =========================================================
   INTERNAL STATE (ANTI-SPAM)
========================================================= */
const activeToasts = new Map(); // key -> toastId
const DEFAULT_THROTTLE_MS = 2500;

/* =========================================================
   HELPERS
========================================================= */
const isMobile = () => window.innerWidth <= 768;

const baseOptions = {
  autoClose: 6000,        // ⏱️ was 3000 → now 6s
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  theme: "colored",
};


const getPosition = () =>
  isMobile() ? "bottom-center" : "top-center";

/* =========================================================
   CORE THROTTLED TOAST
========================================================= */
const throttledToast = (
  type,
  message,
  {
    throttleKey = message,
    throttleMs = DEFAULT_THROTTLE_MS,
    ...options
  } = {}
) => {
  // 🚫 Prevent spam: same toast already active
  if (activeToasts.has(throttleKey)) return;

  const toastId = toast[type](message, {
    ...baseOptions,
    position: getPosition(),
    ...options,
    onClose: () => {
      activeToasts.delete(throttleKey);
      options.onClose?.();
    },
  });

  activeToasts.set(throttleKey, toastId);

  // ⏱ Auto-release throttle (failsafe)
  setTimeout(() => {
    activeToasts.delete(throttleKey);
  }, throttleMs);
};

/* =========================================================
   PUBLIC API
========================================================= */
export const toastSuccess = (message, options = {}) =>
  throttledToast("success", message, options);

export const toastError = (message, options = {}) =>
  throttledToast("error", message, {
    throttleMs: 4000, // errors linger longer
    ...options,
  });

export const toastInfo = (message, options = {}) =>
  throttledToast("info", message, options);

export const toastWarning = (message, options = {}) =>
  throttledToast("warn", message, options);

/* =========================================================
   PROMISE TOAST (ANTI-SPAM SAFE)
========================================================= */
export const toastPromise = (
  promise,
  messages,
  options = {}
) => {
  const key = messages?.pending || "promise-toast";

  if (activeToasts.has(key)) return;

  const toastId = toast.promise(
    promise,
    {
      pending: messages.pending || "Processing…",
      success: messages.success || "Done!",
      error: messages.error || "Something went wrong",
    },
    {
      ...baseOptions,
      position: getPosition(),
      ...options,
      onClose: () => {
        activeToasts.delete(key);
        options.onClose?.();
      },
    }
  );

  activeToasts.set(key, toastId);
};
